import { normalizePhone } from './firebase';

export interface ReceiptItemShare {
    fraction?: number;
    mode?: string;
    name?: string;
    [key: string]: any;
}

export interface ReceiptBreakdownItem {
    id?: string;
    name?: string;
    amount?: number;
    price?: number;
    shares?: Record<string, ReceiptItemShare | number>;
    baseShares?: Record<string, ReceiptItemShare | number>;
    [key: string]: any;
}

export interface ParticipantTaxTipOptions {
    items: ReceiptBreakdownItem[];
    tax?: number;
    tip?: number;
    fees?: number;
    discounts?: number;
    deductions?: number;
    totalSubtotal?: number;
    includeOrganizer?: boolean;
    organizerPhone?: string;
    organizerName?: string;
    payerPhone?: string;
    payerName?: string;
    invites?: any[];
    participants?: Record<string, any>;
    viewerPhone?: string;
    resolveDisplayName?: (phone: string, rawName?: string, isOrganizer?: boolean) => string;
}

export interface ParticipantTaxTipAllocation {
    key: string;
    phone: string;
    displayName: string;
    isOrganizer: boolean;
    isPayer: boolean;
    isViewer: boolean;
    itemsSubtotal: number;
    ratio: number;
    percentage: number;
    taxShare: number;
    tipShare: number;
    feesShare: number;
    discountsShare: number;
    deductionsShare: number;
    totalExtrasShare: number;
}

export const roundMoney = (val: number): number => Math.round((val + Number.EPSILON) * 100) / 100;

export const selectViewerTaxTipBreakdown = (
    allocations: ParticipantTaxTipAllocation[]
): ParticipantTaxTipAllocation[] => allocations.filter((allocation) => allocation.isViewer);

export const computeParticipantTaxTipBreakdown = (
    options: ParticipantTaxTipOptions
): ParticipantTaxTipAllocation[] => {
    const {
        items = [],
        tax = 0,
        tip = 0,
        fees = 0,
        discounts = 0,
        deductions = 0,
        totalSubtotal: explicitTotalSubtotal,
        includeOrganizer = true,
        organizerPhone,
        organizerName,
        payerPhone,
        payerName,
        invites = [],
        participants = {},
        viewerPhone,
        resolveDisplayName,
    } = options;

    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    const normOrganizerPhone = normalizePhone(organizerPhone || payerPhone || '');
    const normViewerPhone = normalizePhone(viewerPhone || '');

    interface ParticipantAccumulator {
        canonicalKey: string;
        phone: string;
        name: string;
        isOrganizer: boolean;
        isPayer: boolean;
        subtotal: number;
    }

    const accumulators = new Map<string, ParticipantAccumulator>();
    let calculatedReceiptSubtotal = 0;

    items.forEach((item) => {
        const itemAmount = Math.max(0, Number(item?.amount ?? item?.price ?? 0));
        calculatedReceiptSubtotal += itemAmount;

        const shares = item?.shares || item?.baseShares || {};
        Object.entries(shares).forEach(([rawKey, shareVal]) => {
            const fraction = Math.max(
                0,
                typeof shareVal === 'number'
                    ? shareVal
                    : Number((shareVal as ReceiptItemShare)?.fraction || 0)
            );
            if (fraction <= 0.00001) return;

            const shareName = typeof shareVal === 'object' ? String((shareVal as any)?.name || '').trim() : '';
            const normalizedKey = normalizePhone(rawKey);
            const isOrganizerShare =
                rawKey === 'organizer' ||
                rawKey === 'payer' ||
                (!!normOrganizerPhone && normalizedKey === normOrganizerPhone);

            const canonicalKey = isOrganizerShare ? 'organizer' : (normalizedKey || rawKey);

            let existing = accumulators.get(canonicalKey);
            if (!existing) {
                existing = {
                    canonicalKey,
                    phone: isOrganizerShare ? normOrganizerPhone : (normalizedKey || rawKey),
                    name: shareName,
                    isOrganizer: isOrganizerShare,
                    isPayer: isOrganizerShare,
                    subtotal: 0,
                };
                accumulators.set(canonicalKey, existing);
            } else if (!existing.name && shareName) {
                existing.name = shareName;
            }

            existing.subtotal += itemAmount * fraction;
        });
    });

    const safeTotalSubtotal = Math.max(
        0.01,
        explicitTotalSubtotal && explicitTotalSubtotal > 0
            ? Math.max(explicitTotalSubtotal, calculatedReceiptSubtotal)
            : calculatedReceiptSubtotal
    );

    // Fallback for equal splits where items exist but have no individual participant shares
    if (accumulators.size === 0) {
        if (includeOrganizer && (normOrganizerPhone || organizerName || payerName)) {
            accumulators.set('organizer', {
                canonicalKey: 'organizer',
                phone: normOrganizerPhone,
                name: organizerName || payerName || 'Organizer',
                isOrganizer: true,
                isPayer: true,
                subtotal: 0,
            });
        }

        if (Array.isArray(invites)) {
            invites.forEach((inv: any, idx: number) => {
                const phone = normalizePhone(inv?.phone || inv?.phoneSnapshot || '');
                const isOrg = !!normOrganizerPhone && phone === normOrganizerPhone;
                if (isOrg && !includeOrganizer) return;
                const canonicalKey = isOrg ? 'organizer' : (phone || inv?.id || `invite_${idx}`);
                if (!accumulators.has(canonicalKey)) {
                    accumulators.set(canonicalKey, {
                        canonicalKey,
                        phone: isOrg ? normOrganizerPhone : phone,
                        name: String(inv?.name || inv?.profileName || '').trim(),
                        isOrganizer: isOrg,
                        isPayer: isOrg,
                        subtotal: 0,
                    });
                }
            });
        }

        if (participants && typeof participants === 'object') {
            if (Array.isArray(participants)) {
                participants.forEach((p: any, idx: number) => {
                    const phone = normalizePhone(p?.phone || p?.id || '');
                    const isOrg = !!normOrganizerPhone && phone === normOrganizerPhone;
                    if (isOrg && !includeOrganizer) return;
                    const canonicalKey = isOrg ? 'organizer' : (phone || p?.id || `participant_${idx}`);
                    if (!accumulators.has(canonicalKey)) {
                        accumulators.set(canonicalKey, {
                            canonicalKey,
                            phone: isOrg ? normOrganizerPhone : phone,
                            name: String(p?.name || p?.displayName || '').trim(),
                            isOrganizer: isOrg,
                            isPayer: isOrg,
                            subtotal: 0,
                        });
                    }
                });
            } else {
                Object.entries(participants).forEach(([pKey, pVal]: [string, any]) => {
                    const phone = normalizePhone(pVal?.phone || pKey || '');
                    const isOrg = phone === 'organizer' || phone === 'payer' || (!!normOrganizerPhone && phone === normOrganizerPhone);
                    if (isOrg && !includeOrganizer) return;
                    const canonicalKey = isOrg ? 'organizer' : (phone || pKey);
                    if (!accumulators.has(canonicalKey)) {
                        accumulators.set(canonicalKey, {
                            canonicalKey,
                            phone: isOrg ? normOrganizerPhone : phone,
                            name: String(pVal?.name || pVal?.displayName || '').trim(),
                            isOrganizer: isOrg,
                            isPayer: isOrg,
                            subtotal: 0,
                        });
                    }
                });
            }
        }

        if (accumulators.size === 0 && normViewerPhone) {
            accumulators.set('viewer', {
                canonicalKey: 'viewer',
                phone: normViewerPhone,
                name: 'You',
                isOrganizer: false,
                isPayer: false,
                subtotal: 0,
            });
        }

        const participantCount = Math.max(1, accumulators.size);
        const equalSubtotalPerPerson = safeTotalSubtotal / participantCount;
        accumulators.forEach((acc) => {
            acc.subtotal = equalSubtotalPerPerson;
        });
    }

    const safeTax = Math.max(0, Number(tax) || 0);
    const safeTip = Math.max(0, Number(tip) || 0);
    const safeFees = Math.max(0, Number(fees) || 0);
    const safeDiscounts = Math.max(0, Number(discounts) || 0);
    const safeDeductions = Math.max(0, Number(deductions) || 0);

    const allocations: ParticipantTaxTipAllocation[] = [];

    accumulators.forEach((acc) => {
        const itemsSubtotal = roundMoney(acc.subtotal);
        if (itemsSubtotal <= 0.0001) return;

        const ratio = safeTotalSubtotal > 0 ? (acc.subtotal / safeTotalSubtotal) : 0;
        const percentage = roundMoney(ratio * 100);

        const taxShare = roundMoney(safeTax * ratio);
        const tipShare = roundMoney(safeTip * ratio);
        const feesShare = roundMoney(safeFees * ratio);
        const discountsShare = roundMoney(safeDiscounts * ratio);
        const deductionsShare = roundMoney(safeDeductions * ratio);
        const totalExtrasShare = roundMoney(
            (safeTax + safeTip + safeFees - safeDiscounts - safeDeductions) * ratio
        );

        // Resolve display name
        let resolvedName = '';
        if (resolveDisplayName) {
            resolvedName = resolveDisplayName(acc.phone, acc.name, acc.isOrganizer);
        }

        if (!resolvedName) {
            if (acc.isOrganizer) {
                resolvedName = organizerName || payerName || acc.name || 'Organizer';
            } else {
                const inviteMatch = invites.find(
                    (inv: any) =>
                        (acc.phone && normalizePhone(inv?.phone || inv?.phoneSnapshot || '') === acc.phone) ||
                        inv?.id === acc.canonicalKey
                );
                const participantData = participants[acc.phone];
                resolvedName =
                    inviteMatch?.name ||
                    inviteMatch?.profileName ||
                    participantData?.name ||
                    acc.name ||
                    (acc.phone ? acc.phone : 'Participant');
            }
        }

        const isViewer = Boolean(
            (acc.isOrganizer && normViewerPhone && normViewerPhone === normOrganizerPhone) ||
            (normViewerPhone && acc.phone && normViewerPhone === acc.phone)
        );

        allocations.push({
            key: acc.canonicalKey,
            phone: acc.phone,
            displayName: resolvedName,
            isOrganizer: acc.isOrganizer,
            isPayer: acc.isPayer,
            isViewer,
            itemsSubtotal,
            ratio,
            percentage,
            taxShare,
            tipShare,
            feesShare,
            discountsShare,
            deductionsShare,
            totalExtrasShare,
        });
    });

    // Sort: Viewer first, then organizer if not viewer, then highest subtotal first
    allocations.sort((a, b) => {
        if (a.isViewer !== b.isViewer) return a.isViewer ? -1 : 1;
        if (a.isOrganizer !== b.isOrganizer) return a.isOrganizer ? -1 : 1;
        return b.itemsSubtotal - a.itemsSubtotal;
    });

    return allocations;
};
